import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, Calendar, Filter, Grid, List, CheckCircle, Clock, AlertCircle, X, ChevronDown, GitBranch, Share2, ExternalLink, AlertTriangle, Copy, Sparkles } from 'lucide-react';
import BookingModal from './BookingModal';
import BulkInvoiceModal from './BulkInvoiceModal';
import { Button, Input, Select } from '../ui';
import api from '../../services/api';
import VersionsModal from '../modern/VersionsModal';
import BookingReconciliationPanelV2 from './BookingReconciliationPanelV2';

interface BookingItinerary {
  itinerary_id: number;
  agency_name: string;
  lead_guest_name: string;
  itinerary_name: string;
  travel_date: string;
  no_of_days: number;
  no_of_pax: number;
  no_of_children: number;
  converted_date: string;
  booking_id: string;
  paging_text: string;
  trip_status: string;
  operation_cost_status: string;
  quotation_id?: number;
  version_number?: number;
  quotation_number?: string;
  itinerary_status?: string;
  has_pending_cancellation?: boolean;
}

const BookingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [allItineraries, setAllItineraries] = useState<BookingItinerary[]>([]);
  const [operationStatistics, setOperationStatistics] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalItineraryId, setModalItineraryId] = useState<number | null>(null);
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const defaultFromDate = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    travel_date_from: defaultFromDate,
    travel_date_to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    agency_name: '',
    quotation_number: '',
    no_of_pax: '',
    no_of_children: '',
    trip_status: '',
    operation_cost_status: '',
    itinerary_status: ''
  });
  const [currentOperationStatus, setCurrentOperationStatus] = useState<string>('ALL');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [sortBy, setSortBy] = useState('travel_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [versionsItineraryId, setVersionsItineraryId] = useState<number | null>(null);
  const [reconciliationItineraryId, setReconciliationItineraryId] = useState<number | null>(null);
  const [shareModal, setShareModal] = useState<{ itineraryId: number; guestName: string } | null>(null);

  useEffect(() => {
    loadOperationStatistics();
    loadItineraries();
    updateActiveFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('Modal state changed:', { showModal, modalItineraryId });
  }, [showModal, modalItineraryId]);

  useEffect(() => {
    loadItineraries();
    setPagination(prev => ({ ...prev, page: 1 }));
    updateActiveFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperationStatus, sortBy, sortOrder, filters]);

  const updateActiveFilters = () => {
    const active: string[] = [];
    if (currentOperationStatus !== 'ALL') active.push(`Operation Status: ${currentOperationStatus}`);
    if (filters.travel_date_from) active.push(`From: ${filters.travel_date_from}`);
    if (filters.travel_date_to) active.push(`To: ${filters.travel_date_to}`);
    if (filters.agency_name) active.push(`Agency: ${filters.agency_name}`);
    if (filters.quotation_number) active.push(`Quotation: ${filters.quotation_number}`);
    if (filters.no_of_pax) active.push(`Pax: ${filters.no_of_pax}`);
    if (filters.no_of_children) active.push(`Children: ${filters.no_of_children}`);
    if (filters.trip_status) active.push(`Trip Status: ${filters.trip_status}`);
    if (filters.itinerary_status) active.push(`Itinerary: ${filters.itinerary_status}`);
    if (filters.operation_cost_status) active.push(`Op Status: ${filters.operation_cost_status}`);
    setActiveFilters(active);
  };

  const loadOperationStatistics = async () => {
    try {
      const response = await api.get('/operational-cost/statistics');
      if (response.data.success) {
        setOperationStatistics(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading operation statistics:', error);
    }
  };

  const loadItineraries = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: '1',
        limit: '10000',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      // Add filter parameters
      if (filters.travel_date_from) params.travel_date_from = filters.travel_date_from;
      if (filters.travel_date_to) params.travel_date_to = filters.travel_date_to;
      
      const response = await api.get('/booking/trips', { params });
      
      if (response.data.success && response.data.data) {
        let filteredData = response.data.data;
        
        // Apply client-side filters
        if (filters.agency_name) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            item.agency_name.toLowerCase().includes(filters.agency_name.toLowerCase())
          );
        }
        if (filters.quotation_number) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            (item.quotation_number || '').toLowerCase().includes(filters.quotation_number.toLowerCase())
          );
        }
        if (filters.no_of_pax) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            item.no_of_pax.toString() === filters.no_of_pax
          );
        }
        if (filters.no_of_children) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            item.no_of_children.toString() === filters.no_of_children
          );
        }
        if (filters.trip_status) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            item.trip_status === filters.trip_status
          );
        }
        if (filters.itinerary_status) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            item.itinerary_status === filters.itinerary_status
          );
        }
        if (currentOperationStatus !== 'ALL') {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            (item.operation_cost_status || 'PENDING') === currentOperationStatus
          );
        }
        if (filters.operation_cost_status) {
          filteredData = filteredData.filter((item: BookingItinerary) => 
            (item.operation_cost_status || 'PENDING') === filters.operation_cost_status
          );
        }
        
        setAllItineraries(filteredData);
      } else {
        setAllItineraries([]);
      }
    } catch (error) {
      console.error('❌ Error loading itineraries:', error);
      setAllItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOperationStatusChange = (status: string) => {
    setCurrentOperationStatus(status);
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getOperationStatusButton = (status: string, count: number, label: string, color: string) => (
    <button
      key={status}
      onClick={() => handleOperationStatusChange(status)}
      className={`px-2 py-2 sm:px-3 sm:py-3 lg:px-4 lg:py-3 rounded-xl border transition-all text-center hover:shadow-md min-h-[60px] sm:min-h-[70px] ${
        currentOperationStatus === status ? 'ring-2 ring-purple-500 bg-purple-50' : ''
      } ${color}`}
    >
      <div className="text-xs sm:text-sm font-semibold truncate leading-tight">{label}</div>
      <div className="text-lg sm:text-xl lg:text-2xl font-bold mt-1">{count}</div>
    </button>
  );

  const markOperationComplete = async (itineraryId: number) => {
    try {
      // Fetch booking details for validation
      const detailsRes = await api.get(`/booking/details/${itineraryId}`);
      if (!detailsRes.data.success) {
        alert('❌ Failed to load booking details for validation.');
        return;
      }
      const data = detailsRes.data.data;
      const errors: string[] = [];

      // Check driver assigned
      if (!data.transportation_handled_by || data.transportation_handled_by.trim() === '') {
        errors.push('Driver name must be assigned');
      }

      // Check transportation booked
      const transportItems = data.transportation_cost_items || [];
      const transportStatus = transportItems.find((t: any) => t.status)?.status || 'NOT_BOOKED';
      if (transportStatus === 'NOT_BOOKED') {
        errors.push('Transportation must be booked');
      }

      // No items should be BOOKED or NOT_BOOKED
      const invalidStatuses = ['BOOKED', 'NOT_BOOKED'];
      const accInvalid = (data.accommodation_cost_items || []).filter((i: any) => invalidStatuses.includes(i.booking_status || 'NOT_BOOKED'));
      const preInvalid = (data.prebooked_cost_items || []).filter((i: any) => invalidStatuses.includes(i.booking_status || 'NOT_BOOKED'));

      if (accInvalid.length > 0) {
        errors.push(`${accInvalid.length} accommodation item(s) still in Booked/Not Booked status`);
      }
      if (preInvalid.length > 0) {
        errors.push(`${preInvalid.length} prebooked item(s) still in Booked/Not Booked status`);
      }

      if (errors.length > 0) {
        alert(`Cannot mark as complete. Please fix the following issues:\n\n${errors.join('\n')}`);
        return;
      }

      if (!window.confirm('✅ Mark Operation Complete\n\nThis will mark the operational cost status as COMPLETED. This action cannot be undone.\n\nContinue?')) return;
      const response = await api.put(`/booking/mark-complete/${itineraryId}`);
      if (response.data.success) {
        alert('✅ Operation marked as complete successfully!');
        loadOperationStatistics();
        loadItineraries();
      } else {
        alert('❌ Failed to mark operation as complete.');
      }
    } catch (error: any) {
      alert(`❌ Error: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'INPROGRESS':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    }
  };

  // Client-side filtering and pagination
  const filteredItineraries = allItineraries.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const idMatch = `wh_${item.itinerary_id}`.includes(query) || item.itinerary_id.toString().includes(query);
    const nameMatch = item.lead_guest_name.toLowerCase().includes(query);
    return idMatch || nameMatch;
  });

  const totalFiltered = filteredItineraries.length;
  const totalPages = Math.ceil(totalFiltered / pagination.limit);
  const startIndex = (pagination.page - 1) * pagination.limit;
  const paginatedItineraries = filteredItineraries.slice(startIndex, startIndex + pagination.limit);

  useEffect(() => {
    setPagination(prev => ({ ...prev, total: totalFiltered, pages: totalPages }));
  }, [totalFiltered, totalPages]);

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-1 overflow-y-auto pb-20">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Manage accommodation and activity bookings for your trips</p>
        </div>

        {/* Operation Cost Status Statistics */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Operation Cost Status</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            {getOperationStatusButton('ALL', (operationStatistics.PENDING || 0) + (operationStatistics.INPROGRESS || 0) + (operationStatistics.COMPLETED || 0), '📋 ALL', 'bg-gray-100 border-gray-300 hover:bg-gray-200')}
            {getOperationStatusButton('PENDING', operationStatistics.PENDING || 0, '⏳ PENDING', 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200')}
            {getOperationStatusButton('INPROGRESS', operationStatistics.INPROGRESS || 0, '🔄 IN PROGRESS', 'bg-blue-100 border-blue-300 hover:bg-blue-200')}
            {getOperationStatusButton('COMPLETED', operationStatistics.COMPLETED || 0, '✅ COMPLETED', 'bg-green-100 border-green-300 hover:bg-green-200')}
          </div>
        </div>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Active Filters:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter, index) => (
                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {filter}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2 sm:gap-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {searchQuery ? `Search: "${searchQuery}"` : 
               currentOperationStatus === 'ALL' ? 'All Trips' : 
               `${currentOperationStatus} Trips`}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              {totalFiltered} {totalFiltered === 1 ? 'trip' : 'trips'} found
              {pagination.pages > 1 && (
                <span className="hidden sm:inline"> • Page {pagination.page} of {pagination.pages}</span>
              )}
            </p>
          </div>
          
          {loading && (
            <RefreshCw className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm mb-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Search Bar */}
            <Input
              type="search"
              placeholder="Search by ID (with or without WH_) or Guest Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
            />
            
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              Filters
            </button>

            {/* Filters Panel */}
            {showFilters && (
              <div className="pt-3 sm:pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                  <Input
                    placeholder="From Date"
                    type="date"
                    value={filters.travel_date_from}
                    onChange={(e) => setFilters({...filters, travel_date_from: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="To Date"
                    type="date"
                    value={filters.travel_date_to}
                    onChange={(e) => setFilters({...filters, travel_date_to: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Agency Name"
                    value={filters.agency_name}
                    onChange={(e) => setFilters({...filters, agency_name: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Quotation (e.g. IQ-0001)"
                    value={filters.quotation_number}
                    onChange={(e) => setFilters({...filters, quotation_number: e.target.value})}
                    className="text-sm"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <Select
                    value={filters.itinerary_status}
                    onChange={(e) => setFilters({...filters, itinerary_status: e.target.value})}
                    className="text-sm"
                  >
                    <option value="">All Itinerary Status</option>
                    <option value="CONVERTED">Converted</option>
                    <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                    <option value="TENTATIVE">Tentative</option>
                    <option value="PENDING_CANCELLATION">Pending Cancellation</option>
                    <option value="CANCELED">Canceled</option>
                  </Select>
                  <Select
                    value={filters.trip_status}
                    onChange={(e) => setFilters({...filters, trip_status: e.target.value})}
                    className="text-sm"
                  >
                    <option value="">All Trip Status</option>
                    <option value="Completed">Completed</option>
                    <option value="Not Completed">Not Completed</option>
                  </Select>
                  <Select
                    value={filters.operation_cost_status}
                    onChange={(e) => setFilters({...filters, operation_cost_status: e.target.value})}
                    className="text-sm"
                  >
                    <option value="">All Operation Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="INPROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                  </Select>
                  <div className="flex gap-2">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-sm flex-1"
                    >
                      <option value="travel_date">Travel Date</option>
                      <option value="itinerary_id">ID</option>
                      <option value="lead_guest_name">Guest Name</option>
                    </Select>
                    <Button
                      variant="secondary"
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                      className="px-2 py-1.5 text-xs"
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setFilters({
                          travel_date_from: defaultFromDate,
                          travel_date_to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
                          agency_name: '',
                          quotation_number: '',
                          no_of_pax: '',
                          no_of_children: '',
                          trip_status: '',
                          operation_cost_status: '',
                          itinerary_status: ''
                        });
                        setCurrentOperationStatus('ALL');
                      }}
                      className="px-2 py-1.5 text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Controls */}
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <Button 
                variant="secondary" 
                icon={RefreshCw} 
                onClick={() => {
                  loadOperationStatistics();
                  loadItineraries();
                }}
                className="text-sm"
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>

              <Button
                variant="secondary"
                onClick={() => setShowBulkInvoiceModal(true)}
                className="text-sm"
              >
                Bulk Invoice
              </Button>

              <div className="hidden sm:flex bg-white border border-gray-300 rounded-xl overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-3 transition-all ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-3 transition-all ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Itinerary List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-gray-600">Loading trips...</p>
            </div>
          ) : paginatedItineraries.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No trips found</p>
            </div>
          ) : (
            paginatedItineraries.map((item) => (
              <div key={item.itinerary_id} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="mb-2 flex items-center gap-2 flex-wrap">
                      {item.quotation_number ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs sm:text-sm font-mono font-bold rounded whitespace-nowrap cursor-pointer hover:bg-blue-200"
                          onClick={(e) => { e.stopPropagation(); setVersionsItineraryId(item.itinerary_id); }}
                          title="View all versions"
                        >
                          <GitBranch className="w-3 h-3" />
                          {item.quotation_number}/{item.version_number}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs sm:text-sm font-mono font-bold rounded whitespace-nowrap">WH_{item.itinerary_id}</span>
                      )}
                      {item.quotation_number && (
                        <span className="text-xs text-gray-400 font-normal">WH_{item.itinerary_id}</span>
                      )}
                      {item.quotation_number && item.version_number && item.version_number > 1 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReconciliationItineraryId(item.itinerary_id); }}
                          className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100"
                        >
                          🔍 Review Bookings
                        </button>
                      )}
                      {item.has_pending_cancellation && (
                        <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 border border-red-300 rounded animate-pulse" title="Active bookings need to be cancelled with vendors">
                          🚨 Cancel Bookings
                        </span>
                      )}
                      {getStatusIcon(item.operation_cost_status)}
                    </div>
                    <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-2 break-words line-clamp-2">{item.itinerary_name}</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 lg:mb-0">
                      <span className="text-xs text-gray-600 truncate max-w-[120px] sm:max-w-none">{item.lead_guest_name}</span>
                      <span className="text-xs text-gray-600 whitespace-nowrap">{new Date(item.travel_date).toLocaleDateString('en-GB')}</span>
                      <span className="text-xs text-gray-600 whitespace-nowrap">{item.no_of_pax} pax</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <span className={`px-3 py-2 rounded-full text-xs font-medium text-center whitespace-nowrap ${
                      item.operation_cost_status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                      item.operation_cost_status === 'INPROGRESS' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      BOOKING: {item.operation_cost_status || 'PENDING'}
                    </span>
                    <Button
                      variant="primary"
                      icon={Calendar}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setModalItineraryId(item.itinerary_id);
                        setShowModal(true);
                      }}
                      className="w-full sm:w-auto whitespace-nowrap px-2 py-1.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
                    >
                      <span className="hidden sm:inline">Manage Booking</span>
                      <span className="sm:hidden">Manage</span>
                    </Button>
                    <Button
                      variant="primary"
                      icon={Sparkles}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        navigate(`/booking/${item.itinerary_id}/manage`);
                      }}
                      className="w-full sm:w-auto whitespace-nowrap px-2 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
                    >
                      <span className="hidden sm:inline">Manage Booking (V4)</span>
                      <span className="sm:hidden">V4</span>
                    </Button>
                    <Button
                      variant="primary"
                      icon={Share2}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setShareModal({ itineraryId: item.itinerary_id, guestName: item.lead_guest_name });
                      }}
                      className="w-full sm:w-auto whitespace-nowrap px-2 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    >
                      <span className="hidden sm:inline">Share Booking</span>
                      <span className="sm:hidden">Share</span>
                    </Button>
                    {item.operation_cost_status !== 'COMPLETED' && (
                      <Button
                        variant="primary"
                        icon={CheckCircle}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          markOperationComplete(item.itinerary_id);
                        }}
                        className="w-full sm:w-auto whitespace-nowrap px-2 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                      >
                        <span className="hidden sm:inline">Mark Complete</span>
                        <span className="sm:hidden">Complete</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>



        {/* Modal */}
        {showModal && modalItineraryId && (
          <BookingModal
            itineraryId={modalItineraryId}
            onClose={() => {
              console.log('Modal closing');
              setShowModal(false);
              setModalItineraryId(null);
              loadItineraries();
            }}
          />
        )}

        <BulkInvoiceModal
          isOpen={showBulkInvoiceModal}
          onClose={() => setShowBulkInvoiceModal(false)}
          onSuccess={() => {
            loadOperationStatistics();
            loadItineraries();
          }}
        />

        {versionsItineraryId && (
          <VersionsModal
            itineraryId={versionsItineraryId}
            onClose={() => setVersionsItineraryId(null)}
          />
        )}

        {reconciliationItineraryId && (
          <BookingReconciliationPanelV2
            itineraryId={reconciliationItineraryId}
            onClose={() => {
              setReconciliationItineraryId(null);
              loadItineraries();
            }}
          />
        )}

        {shareModal && (
          <ShareBookingModal
            itineraryId={shareModal.itineraryId}
            guestName={shareModal.guestName}
            onClose={() => setShareModal(null)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Share Booking Modal ─────────────────────────────────────────

const ShareBookingModal: React.FC<{ itineraryId: number; guestName: string; onClose: () => void }> = ({ itineraryId, guestName, onClose }) => {
  const [readiness, setReadiness] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState(() => String(Math.floor(1000 + Math.random() * 9000)));
  const [sharing, setSharing] = useState(false);
  const [result, setResult] = useState<{ token: string; url: string; already_existed: boolean } | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const PORTAL_DOMAIN = process.env.REACT_APP_PORTAL_DOMAIN || 'https://balify.me';

  useEffect(() => {
    checkReadiness();
  }, [itineraryId]);

  const checkReadiness = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/booking-portal/readiness/${itineraryId}`);
      setReadiness(res.data);
      if (res.data.existing_token) {
        setResult({ token: res.data.existing_token, url: `/${res.data.existing_token}`, already_existed: true });
        if (res.data.existing_password) {
          setPassword(res.data.existing_password);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to check readiness');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (force: boolean = false) => {
    try {
      setSharing(true);
      setError('');
      const res = await api.post(`/booking-portal/share/${itineraryId}`, { password, force });
      if (res.data.success) {
        setResult(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to share booking');
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    if (!result) return;
    const msg = `✈️ *Your Bali Trip Details are Ready!*\n\n` +
      `Hi ${guestName},\n\n` +
      `Your personalised trip itinerary is now available. View your hotel details, activities, driver info and day-by-day schedule here:\n\n` +
      `🔗 ${PORTAL_DOMAIN}${result.url}\n\n` +
      `🔑 Access Code: *${password}*\n\n` +
      `_Open the link and enter your access code to view your trip details._`;
    navigator.clipboard.writeText(msg);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Share Booking with Customer</h2>
            <p className="text-sm text-gray-500">WH_{itineraryId} · {guestName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin w-6 h-6 text-blue-600" />
              <span className="ml-2 text-gray-600">Checking readiness...</span>
            </div>
          ) : result ? (
            /* ─── Success / Already Shared ─── */
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800">
                    {result.already_existed ? 'Already Shared' : 'Booking Shared!'}
                  </span>
                </div>
                <p className="text-sm text-emerald-700 mb-3">Customer can access their booking at:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded-lg text-sm text-gray-900 truncate">
                    {PORTAL_DOMAIN}{result.url}
                  </code>
                  <button onClick={copyLink} className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-1">
                    <Copy className="w-4 h-4" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-emerald-700">Access Code:</span>
                  <code className="px-3 py-1.5 bg-white border border-emerald-300 rounded-lg text-lg font-mono font-bold text-gray-900 tracking-widest">{password}</code>
                </div>
              </div>
              <a
                href={`${PORTAL_DOMAIN}${result.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="w-4 h-4" />
                Open Portal Preview
              </a>
            </div>
          ) : (
            /* ─── Readiness Check + Share Form ─── */
            <>
              {/* Readiness Status */}
              {readiness && (
                <div className={`p-4 rounded-xl border ${
                  readiness.ready ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {readiness.ready ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={`font-semibold text-sm ${readiness.ready ? 'text-green-800' : 'text-amber-800'}`}>
                      {readiness.ready ? 'Ready to share' : 'Not fully ready'}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      {readiness.driver_assigned ? <CheckCircle className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}
                      <span className={readiness.driver_assigned ? 'text-green-700' : 'text-red-700'}>Driver assigned</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {readiness.all_booked ? <CheckCircle className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}
                      <span className={readiness.all_booked ? 'text-green-700' : 'text-red-700'}>All items booked</span>
                    </div>
                  </div>
                  {readiness.warnings.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-amber-200">
                      {readiness.warnings.map((w: string, i: number) => (
                        <p key={i} className="text-xs text-amber-700">⚠️ {w}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Access Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-lg font-mono font-bold text-gray-900 tracking-widest text-center">{password}</code>
                  <button
                    onClick={() => setPassword(String(Math.floor(1000 + Math.random() * 9000)))}
                    className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Regenerate
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Share this 4-digit PIN with the customer</p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}

              {/* Action Buttons */}
              {readiness?.ready && (
                <button
                  onClick={() => handleShare(false)}
                  disabled={sharing}
                  className="w-full py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-40 text-sm"
                >
                  {sharing ? 'Sharing...' : 'Share Booking'}
                </button>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-gray-200">
          <button onClick={onClose} className="w-full py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
        </div>
      </div>
    </div>
  );
};

export default BookingDashboard;